import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import axios from 'src/lib/axios';
import { type MutationConfig } from 'src/lib/react-query';
import { type GeneralSmartParkError } from 'src/types';
import { validationMessagesMap } from 'src/utils/messages';
import { formatResourcePath } from 'src/utils/regex';

import type { PricingPlan } from 'src/features/pricingPlans/types';

const RESOURCE = '/parkings/:parkingId/pricing-plans/normal/';

type FormSchemaValues = z.infer<typeof formSchema>;
const formSchema = z.object({
    plan_name: z.string().min(5, { message: validationMessagesMap.validationErrorPricingPlanName }),
    initial_price: z.number(),
    free_time: z.number(),
    vat: z.number(),
    time_to_leave: z.number(),
    time_to_leave_option: z.string(),
    first_period: z
        .array(
            z.object({
                duration: z.number(),
                price_tick: z.number(),
                due_amount: z.number(),
            }),
        )
        .min(1)
        .max(1),
    second_period: z
        .array(
            z.object({
                duration: z.number(),
                price_tick: z.number(),
                due_amount: z.number(),
            }),
        )
        .min(1)
        .max(1),
    third_period: z
        .array(
            z.object({
                price_tick: z.number(),
                due_amount: z.number(),
            }),
        )
        .min(1)
        .max(1),
    special_period: z
        .array(
            z.object({
                in_use: z.boolean(),
                period_start: z.number(),
                period_end: z.number(),
                price_tick: z.number(),
                due_amount: z.number(),
            }),
        )
        .min(1)
        .max(1),
});

interface DTO extends FormSchemaValues {
    parkingId: string;
}

type MutationFnType = typeof mutationFn;
const mutationFn = ({ parkingId, ...data }: DTO): Promise<PricingPlan> => {
    return axios.post(formatResourcePath(RESOURCE, { parkingId }), data);
};

interface UseMutationOptions extends MutationConfig<MutationFnType, GeneralSmartParkError> {}
const useMutationHook = (options: UseMutationOptions = {}) =>
    useMutation({
        mutationFn,
        ...options,
    });

export {
    RESOURCE as CREATE_PRICING_PLAN_RESOURCE,
    useMutationHook as useCreatePricingPlan,
    formSchema as createPricingPlanFormSchema,
};
export type { DTO as CreatePricingPlanDTO, FormSchemaValues as CreatePricingPlanFormSchemaValues };
